/**
 * Lead list state machine - replaces useCRMStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { Lead, LeadActivity, Pipeline, LeadFilters, CRMStats } from '../types'

const defaultFilters: LeadFilters = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  limit: 20,
}

export interface LeadListContext {
  leads: Lead[]
  selectedLead: Lead | null
  activities: Record<string, LeadActivity[]>
  pipelines: Pipeline[]
  activePipelineId: string | null
  stats: CRMStats | null
  filters: LeadFilters
  totalLeads: number
  isLoading: boolean
}

export type LeadListEvent =
  | { type: 'SET_LEADS'; leads: Lead[]; total: number }
  | { type: 'ADD_LEAD'; lead: Lead }
  | { type: 'UPDATE_LEAD'; leadId: string; data: Partial<Lead> }
  | { type: 'REMOVE_LEAD'; leadId: string }
  | { type: 'SET_SELECTED_LEAD'; lead: Lead | null }
  | { type: 'MOVE_LEAD_TO_STAGE'; leadId: string; status: Lead['status'] }
  | { type: 'SET_ACTIVITIES'; leadId: string; activities: LeadActivity[] }
  | { type: 'ADD_ACTIVITY'; leadId: string; activity: LeadActivity }
  | { type: 'SET_PIPELINES'; pipelines: Pipeline[] }
  | { type: 'SET_ACTIVE_PIPELINE'; pipelineId: string }
  | { type: 'SET_STATS'; stats: CRMStats }
  | { type: 'SET_FILTERS'; filters: Partial<LeadFilters> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as LeadListContext,
    events: {} as LeadListEvent,
  },
})

export const leadListMachine = machineSetup.createMachine({
  id: 'leadList',
  context: {
    leads: [],
    selectedLead: null,
    activities: {},
    pipelines: [],
    activePipelineId: null,
    stats: null,
    filters: defaultFilters,
    totalLeads: 0,
    isLoading: false,
  },
  on: {
    SET_LEADS: {
      actions: assign({
        leads: ({ event }) => (event.type === 'SET_LEADS' ? event.leads : []),
        totalLeads: ({ event }) => (event.type === 'SET_LEADS' ? event.total : 0),
      }),
    },
    ADD_LEAD: {
      actions: assign({
        leads: ({ context, event }) =>
          event.type === 'ADD_LEAD' ? [event.lead, ...context.leads] : context.leads,
        totalLeads: ({ context, event }) =>
          event.type === 'ADD_LEAD' ? context.totalLeads + 1 : context.totalLeads,
      }),
    },
    UPDATE_LEAD: {
      actions: assign({
        leads: ({ context, event }) => {
          if (event.type !== 'UPDATE_LEAD') return context.leads
          return context.leads.map((l) => (l.id === event.leadId ? { ...l, ...event.data } : l))
        },
        selectedLead: ({ context, event }) => {
          if (event.type !== 'UPDATE_LEAD') return context.selectedLead
          if (context.selectedLead?.id !== event.leadId) return context.selectedLead
          return { ...context.selectedLead, ...event.data }
        },
      }),
    },
    REMOVE_LEAD: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'REMOVE_LEAD') return {}
        const { [event.leadId]: _, ...restActivities } = context.activities
        const existed = context.leads.some((l) => l.id === event.leadId)
        return {
          leads: context.leads.filter((l) => l.id !== event.leadId),
          totalLeads: existed ? context.totalLeads - 1 : context.totalLeads,
          selectedLead: context.selectedLead?.id === event.leadId ? null : context.selectedLead,
          activities: restActivities,
        }
      }),
    },
    SET_SELECTED_LEAD: {
      actions: assign({
        selectedLead: ({ event }) => (event.type === 'SET_SELECTED_LEAD' ? event.lead : null),
      }),
    },
    MOVE_LEAD_TO_STAGE: {
      actions: assign({
        leads: ({ context, event }) => {
          if (event.type !== 'MOVE_LEAD_TO_STAGE') return context.leads
          return context.leads.map((l) =>
            l.id === event.leadId
              ? {
                  ...l,
                  status: event.status,
                  updatedAt: new Date().toISOString(),
                }
              : l
          )
        },
        selectedLead: ({ context, event }) => {
          if (event.type !== 'MOVE_LEAD_TO_STAGE') return context.selectedLead
          if (context.selectedLead?.id !== event.leadId) return context.selectedLead
          return {
            ...context.selectedLead,
            status: event.status,
          }
        },
      }),
    },
    SET_ACTIVITIES: {
      actions: assign({
        activities: ({ context, event }) => {
          if (event.type !== 'SET_ACTIVITIES') return context.activities
          return { ...context.activities, [event.leadId]: event.activities }
        },
      }),
    },
    ADD_ACTIVITY: {
      actions: assign({
        activities: ({ context, event }) => {
          if (event.type !== 'ADD_ACTIVITY') return context.activities
          const current = context.activities[event.leadId] || []
          return {
            ...context.activities,
            [event.leadId]: [event.activity, ...current],
          }
        },
      }),
    },
    SET_PIPELINES: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'SET_PIPELINES') return {}
        const activeId =
          context.activePipelineId ||
          (event.pipelines.length > 0
            ? event.pipelines.find((p) => p.isDefault)?.id || event.pipelines[0]?.id || null
            : null)
        return { pipelines: event.pipelines, activePipelineId: activeId }
      }),
    },
    SET_ACTIVE_PIPELINE: {
      actions: assign({
        activePipelineId: ({ event }) =>
          event.type === 'SET_ACTIVE_PIPELINE' ? event.pipelineId : null,
      }),
    },
    SET_STATS: {
      actions: assign({
        stats: ({ event }) => (event.type === 'SET_STATS' ? event.stats : null),
      }),
    },
    SET_FILTERS: {
      actions: assign({
        filters: ({ context, event }) => {
          if (event.type !== 'SET_FILTERS') return context.filters
          return { ...context.filters, ...event.filters }
        },
      }),
    },
    RESET_FILTERS: {
      actions: assign({ filters: defaultFilters }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    CLEAR: {
      actions: assign({
        leads: [],
        selectedLead: null,
        activities: {},
        pipelines: [],
        activePipelineId: null,
        stats: null,
        filters: defaultFilters,
        totalLeads: 0,
        isLoading: false,
      }),
    },
  },
})
